import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useHistory } from 'react-router-dom';

import {
	handleError,
	log
} from '../../../../services/utils';

import { downloadAsset } from '../../../../api/nora/assets';
import {
	getAgencyList,
	exportAgencyList
} from '../../../../api/nora/agencies';

import { useAuthState } from '../../../../contexts/AuthContext';
import {
	useAgencyState,
	useAgencyAction
} from '../../../../contexts/AgenciesContext';
import { useUIAction } from '../../../../contexts/UIContext';

import Email from '../../../Table/Cells/Email';
import HelpModal from '../../../Shared/HelpModal';
import PageHeader from '../../../Shared/PageHeader';
import Table from '../../../Table';
import TableRowActions from '../../../Table/Cells/TableRowActions';

const AgencyList = () => {
	const history = useHistory();

	const [ isLoading, setIsLoading ] = useState( true );

	const { user: { permissions } } = useAuthState();
	const {
		agencyList,
		initialized,
		tableState
	} = useAgencyState();
	const {
		setAgencyList,
		setTableState
	} = useAgencyAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setScreenLoader
	} = useUIAction();

	useEffect( () => {
		setInfobar( <span>Agencies &nbsp;&#9656;&nbsp; Directory</span> );
	}, [ setInfobar ] );

	const retrieveAgencyList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Agency List...' );

		try {
			const data = await getAgencyList();

			setAgencyList( data );
		} catch ( error ) {
			setApiErrorAlert( error );
			throw error;
		}
	}, [
		setAgencyList,
		setApiErrorAlert,
		setInfobarLoader
	] );

	useEffect( () => {
		( async () => {
			if ( !initialized ) {
				setInfobarLoader( 'Please wait...' );
			} else {
				if ( permissions.listAgencies.canExecute && permissions.listAgencies.isAvailable ) {
					try {
						await retrieveAgencyList();
						setInfobarLoader( '' );
					} catch ( error ) {
						if ( error.message !== 'canceled' ) {
							handleError( error );
							setInfobarLoader( '' );
						}
					}
				} else {
					setInfobarLoader( '' );
				}

				setIsLoading( false );
			}
		} )();
	}, [
		initialized,
		permissions,
		retrieveAgencyList,
		setInfobarLoader
	] );

	const emailCell = useCallback( ( { row } ) => <Email email={ row.original.email } />, [] );

	const onView = useCallback( ( { id } ) => {
		history.push( `/agencies/directory/${ id }` );
	}, [ history ] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewAgencies.canExecute,
				icon: 'search',
				permission: permissions.viewAgencies.isAvailable,
				text: 'View Program'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onView,
		permissions
	] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Agency Name',
			filter: 'text',
			minWidth: 60
		},
		{
			id: 'location',
			accessor: 'location',
			Header: 'Location',
			minWidth: 60
		},
		{
			id: 'email',
			accessor: 'email',
			Header: 'Email',
			Cell: emailCell,
			minWidth: 60
		},
		{
			id: 'phone',
			accessor: 'phone',
			Header: 'Phone',
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		emailCell,
		tableRowActionsCell
	] );

	const addAgency = useCallback( () => {
		history.push( '/agencies/directory/0' );
	}, [ history ] );

	const exportAll = useCallback( async () => {
		setScreenLoader( 'Exporting agencies, Please wait...' );

		try {
			const guid = await exportAgencyList( null );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader
	] );

	const exportFiltered = useCallback( async () => {
		setScreenLoader( 'Exporting agencies, Please wait...' );

		try {
			const ids = tableState.rows.map( row => row.original.id );

			const guid = await exportAgencyList( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		tableState.rows
	] );

	const exportSelected = useCallback( async () => {
		setScreenLoader( 'Exporting agencies, Please wait...' );

		try {
			const ids = tableState.selectedIds;

			const guid = await exportAgencyList( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		tableState.selectedIds
	] );

	const actions = useMemo( () => [
		{
			action: {
				'All Rows': exportAll,
				...tableState.filters.length
					? { 'Filtered Rows': exportFiltered }
					: {},
				...tableState.selectedIds.length
					? { 'Selected Rows': exportSelected }
					: {}
			},
			disable: !permissions.listAgencies.canExecute,
			dropdown: true,
			helpId: 4,
			icon: 'cog',
			permission: permissions.listAgencies.isAvailable,
			text: 'Export CSV'
		},
		{
			action: addAgency,
			disable: !permissions.addAgencies.canExecute,
			icon: 'add',
			permission: permissions.addAgencies.isAvailable,
			text: 'Add Agency'
		}
	], [
		addAgency,
		exportAll,
		exportFiltered,
		exportSelected,
		permissions,
		tableState.filters.length,
		tableState.selectedIds.length
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="page">
			{ log( 'agencyList' ) }
			<PageHeader
				actions={ actions }
				title={
					<>
						<span>Agency List</span>
						<HelpModal
							id={ 26 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<Table
				columns={ columns }
				data={ agencyList }
				hasPermission={ permissions.listAgencies.isAvailable }
				selectable
				setTableState={ setTableState }
				tableState={ tableState }
			/>
		</div>
	);
};

export default memo( AgencyList );
